import React, { useState, useEffect } from 'react';
import { Users, Store, Award, ArrowRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useSettings } from '../contexts/SettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getPackages } from '../api/client';

const PackageCard = ({ pkg, onContactClick, isFeatured }) => {
  const { settings } = useSettings();
  const { t, getBilingualValue } = useLanguage();
  const primaryColor = settings.colors?.primary || '#0E7A5F';

  const icons = {
    delegate: Users,
    booth: Store,
    sponsorship: Award
  };

  const IconComponent = icons[pkg.type] || Users;
  const title = getBilingualValue(pkg.title) || t(`packages.${pkg.type}`);
  
  // Get tagline - either from dedicated field or first line of description
  const rawDescription = getBilingualValue(pkg.description) || '';
  const tagline = getBilingualValue(pkg.tagline) || rawDescription.split('\n')[0]?.split('.')[0] || '';

  // Extract features from pkg.features OR parse from newline-separated description
  let features = [];
  if (pkg.features && pkg.features.length > 0) {
    features = pkg.features.slice(0, 6);
  } else if (rawDescription) {
    // Parse description lines as features (for legacy data)
    const lines = rawDescription.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0 && line.length < 50) // Only short lines
      .slice(0, 6);
    features = lines.map(line => ({ en: line, tr: line, id: line }));
  }

  return (
    <div 
      className={`relative flex flex-col h-full bg-white rounded-2xl border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
        isFeatured ? 'border-green-500 shadow-lg' : 'border-gray-100'
      }`}
    >
      {/* Featured Badge */}
      {isFeatured && (
        <div 
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white uppercase tracking-wide"
          style={{ backgroundColor: primaryColor }}
        >
          {t('packages.popular') || 'Most Popular'}
        </div>
      )}

      {/* Card Content */}
      <div className="flex flex-col h-full p-6 pt-8">
        {/* Icon & Title Section */}
        <div className="text-center mb-6">
          <div 
            className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-4"
            style={{ backgroundColor: `${primaryColor}12` }}
          >
            <IconComponent size={28} style={{ color: primaryColor }} />
          </div>
          
          {/* Title - Bold, Large */}
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {title}
          </h3>
          
          {/* Short One-Line Description */}
          {tagline && (
            <p className="text-gray-500 text-sm leading-snug">
              {tagline.length > 60 ? tagline.substring(0, 60) + '...' : tagline}
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 my-4"></div>

        {/* Features List - Bullet Points */}
        <ul className="flex-grow space-y-3 mb-6">
          {features.map((feature, idx) => {
            const featureText = getBilingualValue(feature) || feature;
            // Keep feature text reasonably short
            const shortText = typeof featureText === 'string' && featureText.length > 35 
              ? featureText.substring(0, 35).trim() + '...' 
              : featureText;
            
            return (
              <li key={typeof feature === 'string' ? feature : `feat-${idx}`} className="flex items-start gap-3">
                <div 
                  className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <Check size={12} style={{ color: primaryColor }} strokeWidth={3} />
                </div>
                <span className="text-gray-700 text-sm leading-tight">
                  {shortText}
                </span>
              </li>
            );
          })}
        </ul>

        {/* CTA Button - Always at Bottom */}
        <Button
          onClick={onContactClick}
          data-testid={`package-cta-${pkg.type}`}
          className={`w-full py-3 rounded-xl font-semibold text-base transition-all duration-300 flex items-center justify-center gap-2 group ${
            isFeatured 
              ? 'text-white shadow-md hover:shadow-lg' 
              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
          }`}
          style={isFeatured ? { backgroundColor: primaryColor } : {}}
        >
          {getBilingualValue(pkg.ctaText) || t('packages.contactUs') || 'Contact Us'}
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </Button>
      </div>
    </div>
  );
};

export const PackagesSection = () => {
  const { settings } = useSettings();
  const { t, getBilingualValue } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default packages with SHORT bullet points (3-6 words each)
  const defaultPackages = [
    {
      id: '1',
      type: 'delegate',
      title: { en: 'Delegate Pass', tr: 'Delege Paketi', id: 'Paket Delegasi' },
      tagline: { 
        en: 'For attendees exploring halal opportunities',
        tr: 'Helal fırsatlarını keşfeden katılımcılar için',
        id: 'Untuk peserta yang menjelajahi peluang halal'
      },
      features: [
        { en: 'Full event access', tr: 'Tam etkinlik erişimi', id: 'Akses penuh acara' },
        { en: 'Networking sessions', tr: 'Networking oturumları', id: 'Sesi networking' },
        { en: 'Conference materials', tr: 'Konferans materyalleri', id: 'Materi konferensi' },
        { en: 'Attendance certificate', tr: 'Katılım sertifikası', id: 'Sertifikat kehadiran' },
        { en: 'Refreshments included', tr: 'İkram dahil', id: 'Konsumsi termasuk' }
      ],
      ctaText: { en: 'Contact Us', tr: 'Bize Ulaşın', id: 'Hubungi Kami' },
      featured: false
    },
    {
      id: '2',
      type: 'booth',
      title: { en: 'Exhibition Booth', tr: 'Sergi Standı', id: 'Stan Pameran' },
      tagline: { 
        en: 'Showcase your products globally',
        tr: 'Ürünlerinizi küresel olarak sergileyin',
        id: 'Tampilkan produk Anda secara global'
      },
      features: [
        { en: 'Premium booth space', tr: 'Premium stand alanı', id: 'Ruang stan premium' },
        { en: 'B2B matchmaking', tr: 'B2B eşleştirme', id: 'B2B matchmaking' },
        { en: 'Marketing exposure', tr: 'Pazarlama görünürlüğü', id: 'Eksposur pemasaran' },
        { en: 'Lead generation tools', tr: 'Müşteri adayı araçları', id: 'Alat generasi prospek' },
        { en: '2 delegate passes', tr: '2 delege geçişi', id: '2 tiket delegasi' },
        { en: 'Catalog listing', tr: 'Katalog listesi', id: 'Listing katalog' }
      ],
      ctaText: { en: 'Contact Us', tr: 'Bize Ulaşın', id: 'Hubungi Kami' },
      featured: true
    },
    {
      id: '3',
      type: 'sponsorship',
      title: { en: 'Sponsorship', tr: 'Sponsorluk', id: 'Sponsor' },
      tagline: { 
        en: 'Maximum brand visibility',
        tr: 'Maksimum marka görünürlüğü',
        id: 'Visibilitas merek maksimal'
      },
      features: [
        { en: 'Premium branding', tr: 'Premium marka', id: 'Branding premium' },
        { en: 'Speaking opportunity', tr: 'Konuşma fırsatı', id: 'Kesempatan berbicara' },
        { en: 'VIP networking', tr: 'VIP networking', id: 'Networking VIP' },
        { en: 'Media coverage', tr: 'Medya kapsamı', id: 'Liputan media' },
        { en: 'Premium booth', tr: 'Premium stand', id: 'Stan premium' },
        { en: '5 delegate passes', tr: '5 delege geçişi', id: '5 tiket delegasi' }
      ],
      ctaText: { en: 'Contact Us', tr: 'Bize Ulaşın', id: 'Hubungi Kami' },
      featured: false
    }
  ];

  // Fetch packages from API
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const data = await getPackages();
        if (data.packages && data.packages.length > 0) {
          setPackages(data.packages);
        } else {
          setPackages(defaultPackages);
        }
      } catch {
        setPackages(defaultPackages);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const displayPackages = packages.length > 0 ? packages : defaultPackages;
  const sectionTitle = getBilingualValue(settings.packagesTitle) || t('packages.title') || 'Our Packages';
  const sectionSubtitle = getBilingualValue(settings.packagesSubtitle) || t('packages.subtitle') || 'Choose the perfect package for your participation';

  const scrollToContact = () => {
    const element = document.getElementById('exhibitor');
    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
  };

  return (
    <section id="packages" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-14">
          <h2 className="text-4xl font-bold text-gray-900 mb-3" data-testid="packages-title">
            {sectionTitle}
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            {sectionSubtitle}
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          /* Packages Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {displayPackages.map((pkg, index) => (
              <PackageCard 
                key={pkg.id || index} 
                pkg={pkg} 
                isFeatured={pkg.featured}
                onContactClick={scrollToContact}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
